import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  Info,
  ListPlus,
  Loader2,
  Users,
} from 'lucide-react'
import type { CustomDataArtifact } from '@dev-streetbeat/chat/types'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { IconButton } from '@/components/ui/IconButton'
import { useToast, type ToastVariant } from '@/components/ui/toast'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { addClientTodos, fetchTodos, TODOS_UPDATED_EVENT } from '../app/api/todos'
import type { ClientRef } from './composer-context/types'

import styles from './ClientListArtifact.module.scss'

// chat-ui's ArtifactProps type isn't re-exported from the package root, so
// redeclare the minimal runtime contract.
interface ArtifactProps<T> {
  artifact: T
  onLoaded?: () => void
}

/**
 * Renderer for the credem.client_list v2/v3 custom-data artifact emitted by
 * the agent's render_client_list_artifact MCP tool.
 *
 * v2/v3 schema (positional columns):
 *   { title, columns: string[], clients: [{client_code, bank_code?, values: string[]}],
 *     source_summary, as_of_dt }
 * The card always shows every client the tool returned (completeness is enforced
 * server-side), so the count badge is simply clients.length.
 *
 * - First 3 rows visible, Espandi reveals the rest
 * - Each row links to /clients/{client_code}?bank_code={bank_code} when canonical identity is available
 * - Table scrolls horizontally when columns + values overflow the card
 * - The agent suppresses its markdown table when this artifact is emitted,
 *   so the card is the only place the client list appears
 */

interface ClientListEntry {
  client_code: string
  bank_code?: string | null
  client_uid?: string | null
  values: string[]
}

interface ArtifactSummaryItem {
  label: string
  value: string
}

interface ClientListPayload {
  title: string
  columns: string[]
  clients: ClientListEntry[]
  summary: ArtifactSummaryItem[]
  source_summary: string
  as_of_dt: string
}

const INITIAL_VISIBLE = 3
export const CLIENT_CODE_SELECTED_EVENT = 'credem:client-code-selected'
// Emitted by the right-arrow action on an artifact row. The handler in
// AceConversation sets the chip AND auto-sends the prompt as the user's next
// turn, so the agent routes it through the `client_brief` hint.
export const CLIENT_BRIEF_REQUESTED_EVENT = 'credem:client-brief-requested'

export interface ClientCodeSelectedDetail {
  client: ClientRef
}

export interface ClientBriefRequestedDetail {
  client: ClientRef
  prompt: string
}

// Italian phrasing the agent's `client_brief` hint listens for. The client
// scope is carried by the chip metadata (chat_context.client_code) attached to
// the same turn, so the prompt itself stays client-agnostic. Exported so
// surfaces outside the artifact (e.g. the Da fare row arrow) can route through
// the same hint path.
export const CLIENT_BRIEF_PROMPT =
  'Fai un resoconto della posizione e della performance del cliente considerando lo scenario attuale e genera un brief'

function parsePayload(data: unknown): ClientListPayload | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>

  const columnsRaw = raw.columns
  const columns: string[] = []
  if (Array.isArray(columnsRaw)) {
    for (const c of columnsRaw) {
      if (typeof c === 'string' && c.trim()) columns.push(c.trim())
    }
  }

  const clientsRaw = raw.clients
  if (!Array.isArray(clientsRaw)) return null

  const clients: ClientListEntry[] = []
  for (const item of clientsRaw) {
    if (!item || typeof item !== 'object') continue
    const entry = item as Record<string, unknown>
    const code = typeof entry.client_code === 'string' ? entry.client_code.trim() : ''
    if (!code) continue
    const bankCode = typeof entry.bank_code === 'string' ? entry.bank_code.trim() : ''
    const clientUid = typeof entry.client_uid === 'string' ? entry.client_uid.trim() : ''
    const valuesRaw = entry.values
    const values: string[] = []
    if (Array.isArray(valuesRaw)) {
      for (const v of valuesRaw) {
        values.push(typeof v === 'string' ? v : '')
      }
    }
    while (values.length < columns.length) values.push('')
    clients.push({
      client_code: code,
      bank_code: bankCode || null,
      client_uid: clientUid || null,
      values: values.slice(0, columns.length),
    })
  }

  const summaryRaw = raw.summary
  const summary: ArtifactSummaryItem[] = []
  if (Array.isArray(summaryRaw)) {
    for (const item of summaryRaw) {
      if (!item || typeof item !== 'object') continue
      const entry = item as Record<string, unknown>
      const label = typeof entry.label === 'string' ? entry.label.trim() : ''
      const value = typeof entry.value === 'string' ? entry.value.trim() : ''
      if (!label || !value) continue
      summary.push({ label, value })
      if (summary.length >= 4) break
    }
  }

  return {
    title: typeof raw.title === 'string' && raw.title.trim() ? raw.title.trim() : 'Clienti',
    columns,
    clients,
    summary,
    source_summary: typeof raw.source_summary === 'string' ? raw.source_summary : '',
    as_of_dt: typeof raw.as_of_dt === 'string' ? raw.as_of_dt : '',
  }
}

function formatAsOf(iso: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split('-')
    return `${d}/${m}/${y}`
  }
  return iso
}

// CSV escape — wrap in quotes if value contains comma/quote/newline; escape internal quotes.
function csvCell(value: string): string {
  if (/[",\n;]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function buildCsv(payload: ClientListPayload): string {
  const header = ['Cliente', ...payload.columns].map(csvCell).join(',')
  const rows = payload.clients.map((c) => [c.client_code, ...c.values].map(csvCell).join(','))
  // BOM so Excel opens UTF-8 with accented Italian characters correctly.
  return '﻿' + [header, ...rows].join('\r\n')
}

function slugify(title: string): string {
  return (
    title
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'clienti'
  )
}

function downloadCsv(payload: ClientListPayload): void {
  const csv = buildCsv(payload)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const today = new Date()
  const datePart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  const filename = `${slugify(payload.title)}-${datePart}.csv`
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Defer revoke so the browser has time to trigger the save dialog.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

function clientIdentityKey(client: ClientListEntry): string {
  return client.client_uid || `${client.bank_code || ''}:${client.client_code}`
}

// Matchers for "is this artifact row already in the active todo list?".
//
// Artifact rows may arrive without a `bank_code` (the agent only includes
// it when the source tool returned it). Todos always carry a `bank_code`
// because the backend resolves client_code -> (bank_code, client_code)
// before persisting. So we keep two sets and pick the best match per row:
//
//   * strict — `${bank_code}:${client_code}` for unambiguous identity
//     across the two-bank case (the rare ~6% of advisors who span banks).
//   * loose  — `client_code` alone, used when the artifact row has no bank
//     hint at all. Accepts a benign false positive on the ambiguous-code
//     edge case in exchange for working with bank-less rows.
interface TodoMatchers {
  strict: Set<string>
  loose: Set<string>
}

function buildTodoMatchers(activeTodoClients: Array<{ bank_code?: string | null; client_code: string }>): TodoMatchers {
  const strict = new Set<string>()
  const loose = new Set<string>()
  for (const client of activeTodoClients) {
    const code = (client.client_code || '').trim()
    if (!code) continue
    loose.add(code)
    const bank = (client.bank_code || '').trim()
    if (bank) strict.add(`${bank}:${code}`)
  }
  return { strict, loose }
}

function mergeTodoMatchers(
  current: TodoMatchers,
  clients: Array<{ bank_code?: string | null; client_code: string; client_uid?: string | null }>,
): TodoMatchers {
  const strict = new Set(current.strict)
  const loose = new Set(current.loose)
  for (const client of clients) {
    const code = (client.client_code || '').trim()
    if (!code) continue
    loose.add(code)
    const bank = (client.bank_code || '').trim() || bankFromClientUid(client.client_uid)
    if (bank) strict.add(`${bank}:${code}`)
  }
  return { strict, loose }
}

function combineTodoMatchers(a: TodoMatchers, b: TodoMatchers): TodoMatchers {
  return {
    strict: new Set([...a.strict, ...b.strict]),
    loose: new Set([...a.loose, ...b.loose]),
  }
}

// `client_uid` is "bank:code" when present — give us a bank hint for rows
// whose `bank_code` field was omitted by the source tool.
function bankFromClientUid(uid: string | null | undefined): string {
  if (!uid) return ''
  const [bank] = uid.split(':')
  return (bank || '').trim()
}

function isArtifactRowInTodo(client: ClientListEntry, matchers: TodoMatchers): boolean {
  const code = client.client_code
  if (!code) return false
  const bank = (client.bank_code || '').trim() || bankFromClientUid(client.client_uid)
  if (bank && matchers.strict.has(`${bank}:${code}`)) return true
  return matchers.loose.has(code)
}

function todoRequestClient(client: ClientListEntry): {
  client_code: string
  bank_code?: string
  client_uid?: string
} {
  return {
    client_code: client.client_code,
    ...(client.bank_code ? { bank_code: client.bank_code } : {}),
    ...(client.client_uid ? { client_uid: client.client_uid } : {}),
  }
}

function resultMessage(result: Awaited<ReturnType<typeof addClientTodos>>): string {
  const { added, skipped_existing, not_found, ambiguous } = result.counts
  const unresolved = not_found + ambiguous
  const unresolvedLabel = [
    not_found > 0 ? `${not_found} ${not_found === 1 ? 'non trovato' : 'non trovati'}` : '',
    ambiguous > 0 ? `${ambiguous} ${ambiguous === 1 ? 'ambiguo' : 'ambigui'}` : '',
  ]
    .filter(Boolean)
    .join(', ')
  if (added > 0 && skipped_existing > 0) {
    const suffix = unresolved > 0 ? `, ${unresolvedLabel}.` : '.'
    return `${added} aggiunti, ${skipped_existing} già presenti${suffix}`
  }
  if (added > 0) {
    const suffix = unresolved > 0 ? ` (${unresolvedLabel}).` : '.'
    return `${added} aggiunti ai todo${suffix}`
  }
  if (skipped_existing > 0 && not_found === 0 && ambiguous === 0) {
    return skipped_existing === 1 ? 'Cliente già nei todo.' : 'Clienti già nei todo.'
  }
  if (not_found > 0 || ambiguous > 0) {
    return `Nessun cliente aggiunto: ${unresolvedLabel}.`
  }
  return 'Nessun cliente aggiunto.'
}

function resultToastVariant(result: Awaited<ReturnType<typeof addClientTodos>>): ToastVariant {
  if (result.counts.added > 0) return 'success'
  if (result.counts.not_found > 0 || result.counts.ambiguous > 0) return 'warning'
  return 'info'
}

function clientRefFromCode(clientCode: string): ClientRef {
  return {
    client_code: clientCode,
    client_label: clientCode,
    segment_name: 'PRIVATE BANKER',
    service_name: null,
  }
}

function dispatchClientCodeSelected(clientCode: string, target: EventTarget): void {
  target.dispatchEvent(
    new CustomEvent<ClientCodeSelectedDetail>(CLIENT_CODE_SELECTED_EVENT, {
      detail: { client: clientRefFromCode(clientCode) },
      bubbles: true,
    }),
  )
}

function dispatchClientBriefRequested(client: ClientListEntry, target: EventTarget): void {
  target.dispatchEvent(
    new CustomEvent<ClientBriefRequestedDetail>(CLIENT_BRIEF_REQUESTED_EVENT, {
      detail: {
        client: clientRefFromCode(client.client_code),
        prompt: CLIENT_BRIEF_PROMPT,
      },
      bubbles: true,
    }),
  )
}

function ClippedValue({ value }: { value: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [isClipped, setIsClipped] = useState(false)

  function measure() {
    const node = ref.current
    setIsClipped(Boolean(node && node.scrollWidth > node.clientWidth))
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span ref={ref} className={styles.cellText} onPointerEnter={measure}>
          {value}
        </span>
      </TooltipTrigger>
      {isClipped ? (
        <TooltipContent side="top" align="start" className="break-words">
          {value}
        </TooltipContent>
      ) : null}
    </Tooltip>
  )
}

export function ClientListArtifact({ artifact }: ArtifactProps<CustomDataArtifact>) {
  const payload = useMemo(() => parsePayload(artifact.custom_data?.data), [artifact])
  const { showToast } = useToast()
  const [expanded, setExpanded] = useState(false)
  const [bulkPending, setBulkPending] = useState(false)
  const [pendingClientKeys, setPendingClientKeys] = useState<Set<string>>(() => new Set())
  const [todoMatchers, setTodoMatchers] = useState<TodoMatchers>(() => ({
    strict: new Set<string>(),
    loose: new Set<string>(),
  }))
  const [optimisticTodoMatchers, setOptimisticTodoMatchers] = useState<TodoMatchers>(() => ({
    strict: new Set<string>(),
    loose: new Set<string>(),
  }))
  const [todoRefreshTick, setTodoRefreshTick] = useState(0)

  // Load the set of clients already in the active todo list so we can disable
  // the per-row "add" action for them. Failures are intentionally swallowed —
  // the artifact must keep working even if the todos endpoint is unreachable.
  useEffect(() => {
    const controller = new AbortController()
    fetchTodos(controller.signal)
      .then((todos) => {
        if (controller.signal.aborted) return
        setTodoMatchers(buildTodoMatchers(todos.active.map((todo) => todo.client)))
      })
      .catch(() => {
        // ignore — the row just won't show as already added
      })
    return () => controller.abort()
  }, [todoRefreshTick])

  useEffect(() => {
    const onTodosUpdated = () => setTodoRefreshTick((tick) => tick + 1)
    window.addEventListener(TODOS_UPDATED_EVENT, onTodosUpdated)
    return () => window.removeEventListener(TODOS_UPDATED_EVENT, onTodosUpdated)
  }, [])

  if (!payload || payload.clients.length === 0) return null

  const artifactTitle = payload.title
  const total = payload.clients.length
  const hasMore = total > INITIAL_VISIBLE
  const visible = expanded || !hasMore ? payload.clients : payload.clients.slice(0, INITIAL_VISIBLE)
  const hiddenCount = total - INITIAL_VISIBLE
  const hasColumns = payload.columns.length > 0
  const artifactAsOf = payload.as_of_dt ? formatAsOf(payload.as_of_dt) : ''
  const hasSourceInfo = Boolean(payload.source_summary || artifactAsOf)
  const effectiveTodoMatchers = combineTodoMatchers(todoMatchers, optimisticTodoMatchers)
  // Bulk action disables once every client in the artifact is already in the
  // active todo list. We check against the full payload (not just the rows
  // currently visible) because the button claims to add "tutti i clienti".
  const allInTodo = payload.clients.every((client) => isArtifactRowInTodo(client, effectiveTodoMatchers))
  const bulkActionLabel = allInTodo
    ? 'Tutti i clienti sono già nei todo'
    : 'Aggiungi tutti i clienti ai todo'
  const bulkActionTitle = allInTodo ? 'Tutti già nei todo' : 'Aggiungi tutti ai todo'

  async function addTodosForClients(clients: ClientListEntry[], mode: 'bulk' | 'single') {
    if (clients.length === 0) return
    const keys = clients.map(clientIdentityKey)
    if (mode === 'bulk') {
      setBulkPending(true)
    } else {
      setPendingClientKeys((current) => new Set([...current, ...keys]))
    }
    try {
      const result = await addClientTodos({
        source_type: 'client_list_artifact',
        source_label: artifactTitle,
        clients: clients.map(todoRequestClient),
      })
      setOptimisticTodoMatchers((current) =>
        mergeTodoMatchers(current, [
          ...result.added.map((todo) => todo.client),
          ...result.skipped_existing,
        ]),
      )
      window.dispatchEvent(new CustomEvent(TODOS_UPDATED_EVENT))
      showToast({
        variant: resultToastVariant(result),
        message: resultMessage(result),
      })
    } catch (err) {
      showToast({ variant: 'error', message: (err as Error).message })
    } finally {
      if (mode === 'bulk') {
        setBulkPending(false)
      } else {
        setPendingClientKeys((current) => {
          const next = new Set(current)
          keys.forEach((key) => next.delete(key))
          return next
        })
      }
    }
  }

  return (
    <TooltipProvider>
      <section className={styles.card} aria-label={payload.title}>
        <header className={styles.header}>
          <div className={styles.titleBlock}>
            <Users className={styles.titleIcon} aria-hidden />
            <h4 className={styles.title}>{payload.title}</h4>
          </div>
          <div className={styles.headerMeta}>
            {hasSourceInfo ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" tabIndex={0} className="cursor-help">
                    <Info aria-hidden />
                    Fonti
                  </Badge>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="end"
                  className="w-[min(20rem,calc(100vw-2rem))] flex-col items-start gap-1"
                >
                  {payload.source_summary ? (
                    <span className="block break-words">
                      <span className="font-semibold">Fonte:</span>{' '}
                      <code className="font-mono text-[11px] leading-relaxed">
                        {payload.source_summary}
                      </code>
                    </span>
                  ) : null}
                  {artifactAsOf ? (
                    <span className="block">
                      <span className="font-semibold">Data di validità:</span> {artifactAsOf}
                    </span>
                  ) : null}
                </TooltipContent>
              </Tooltip>
            ) : null}
            <Badge variant="outline">
              {total} {total === 1 ? 'cliente' : 'clienti'}
            </Badge>
            <Button
              variant="outline"
              size="xs"
              onClick={() => addTodosForClients(payload.clients, 'bulk')}
              disabled={bulkPending || allInTodo}
              aria-label={bulkActionLabel}
              aria-pressed={allInTodo}
              title={bulkActionTitle}
            >
              {bulkPending ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : allInTodo ? (
                <Check aria-hidden />
              ) : (
                <ListPlus aria-hidden />
              )}
              Todo
            </Button>
            <Button
              variant="outline"
              size="xs"
              onClick={() => downloadCsv(payload)}
              aria-label="Esporta in CSV (apribile con Excel)"
              title="Esporta in CSV (apribile con Excel)"
            >
              <Download aria-hidden />
              CSV
            </Button>
          </div>
        </header>

        {payload.summary.length > 0 ? (
          <dl className={styles.summary}>
            {payload.summary.map((item, idx) => (
              <div key={`s-${idx}`} className={styles.summaryItem}>
                <dt className={styles.summaryLabel}>{item.label}</dt>
                <dd className={styles.summaryValue}>{item.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.headRow}>
                <th className={styles.th}>Cliente</th>
                {payload.columns.map((col, idx) => (
                  <th key={`col-${idx}`} className={styles.th}>
                    {col}
                  </th>
                ))}
                <th aria-hidden className={`${styles.th} ${styles.thAction}`}></th>
                <th aria-hidden className={`${styles.th} ${styles.thAction}`}></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((client, rowIndex) => {
                const key = clientIdentityKey(client)
                const pending = pendingClientKeys.has(key)
                const alreadyInTodo = isArtifactRowInTodo(client, effectiveTodoMatchers)
                const todoActionLabel = alreadyInTodo
                  ? `Cliente ${client.client_code} già nei todo`
                  : `Aggiungi cliente ${client.client_code} ai todo`
                const todoActionTitle = alreadyInTodo ? 'Già nei todo' : 'Aggiungi ai todo'
                // First-row markers used by the FTUE kit. Only the first
                // visible row is anchorable; subsequent rows are unmarked
                // so the spotlight ring doesn't latch onto a moving target
                // when the agent paginates results.
                const isFirstRow = rowIndex === 0
                return (
                  // tabIndex={-1} makes the row mouse-focusable (click anywhere
                  // in the row → focus lands on the <tr> → `:focus-within` fires
                  // the green selected state) without adding the row to the
                  // keyboard Tab order. Inner buttons keep their natural Tab
                  // stops; screen readers still see the row as a plain row.
                  <tr key={key} className={styles.bodyRow} tabIndex={-1}>
                    <td className={`${styles.cell} ${styles.cellClient}`}>
                      <button
                        type="button"
                        onClick={(event) =>
                          dispatchClientCodeSelected(client.client_code, event.currentTarget)
                        }
                        className={styles.clientCodeButton}
                        aria-label={`Aggiungi cliente ${client.client_code} alla chat`}
                        title="Aggiungi alla chat"
                        data-ftue-step={isFirstRow ? 'client-name-pin' : undefined}
                      >
                        {client.client_code}
                      </button>
                    </td>
                    {hasColumns
                      ? client.values.map((val, idx) => (
                          <td
                            key={`v-${client.client_code}-${idx}`}
                            className={`${styles.cell} ${styles.cellValue}`}
                          >
                            <ClippedValue value={val || '—'} />
                          </td>
                        ))
                      : null}
                    <td className={styles.cellAction}>
                      <IconButton
                        size="xs"
                        aria-label={todoActionLabel}
                        title={todoActionTitle}
                        onClick={() => addTodosForClients([client], 'single')}
                        disabled={pending || bulkPending || alreadyInTodo}
                        aria-pressed={alreadyInTodo}
                      >
                        {pending ? (
                          <Loader2 className="animate-spin" aria-hidden />
                        ) : alreadyInTodo ? (
                          <Check aria-hidden />
                        ) : (
                          <ListPlus aria-hidden />
                        )}
                      </IconButton>
                    </td>
                    <td className={styles.cellAction}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <IconButton
                            size="xs"
                            aria-label={`Genera brief per il cliente ${client.client_code}`}
                            onClick={(event) =>
                              dispatchClientBriefRequested(client, event.currentTarget)
                            }
                            data-ftue-step={
                              isFirstRow ? 'generate-brief-arrow' : undefined
                            }
                          >
                            <ArrowRight aria-hidden />
                          </IconButton>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="end">
                          Genera brief cliente
                        </TooltipContent>
                      </Tooltip>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {hasMore ? (
          <button
            type="button"
            onClick={() => setExpanded((current) => !current)}
            className={styles.expander}
            aria-expanded={expanded}
          >
            {expanded ? (
              <>
                Mostra meno <ChevronUp className={styles.expanderIcon} aria-hidden />
              </>
            ) : (
              <>
                Mostra altri {hiddenCount} <ChevronDown className={styles.expanderIcon} aria-hidden />
              </>
            )}
          </button>
        ) : null}
      </section>
    </TooltipProvider>
  )
}
